<template>
    <div>
        <div v-if="showBreakdown">
            <div v-for="(response, index) in results.responses" :key="response.id">
                <!-- Panel Title -->
                <p class="mb-2">
                    <span class="badge badge-light reporting-badge align-middle">
                        <span class="icon mr-1" :class="getPanelIconClass(response.panel)"></span>
                        {{ response.panel.reporting_label }}
                    </span>
                </p>

                <!-- Scored Choice List -->
                <div v-show="response.scored_choices.length > 0">
                    <ul>
                        <li v-for="scoredChoice in response.scored_choices" :key="scoredChoice.id">
                            <p class="mb-0">
                                <span class="align-middle">
                                    {{ scoredChoice.choice_text }}
                                </span>
                                <span
                                    class="align-middle ml-2"
                                    :class="{
                                        'text-success': scoredChoice.score > 0,
                                        'text-danger': scoredChoice.score < 0,
                                        'text-secondary': scoredChoice.score == 0,
                                    }"
                                    v-show="scoredChoice.score"
                                >
                                    {{ formatPointsDisplay(scoredChoice.score) }} pts
                                </span>
                                <span
                                    v-show="scoredChoice.choice_correct === true"
                                    class="icon icon-lg icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success"
                                ></span>
                                <span
                                    v-show="scoredChoice.choice_correct === false"
                                    class="icon icon-lg icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger"
                                ></span>
                                <span class="small text-secondary ml-1" v-if="displayTimestamps && scoredChoice.created"
                                    >({{ displayDate(scoredChoice.created) }})</span
                                >
                            </p>
                            <div v-if="scoredChoice.provided_value" class="mb-1 text-secondary medium">
                                <p class="mb-0">
                                    {{ labelsTranslated.yourResponse }}: {{ scoredChoice.provided_value }}
                                </p>
                                <p
                                    class="mb-0"
                                    v-show="
                                        scoredChoice.correct_values.length &&
                                        !scoredChoice.correct_values.includes('*')
                                    "
                                >
                                    {{ labelsTranslated.correctResponse }}: {{ scoredChoice.correct_values.join("/") }}
                                </p>
                            </div>
                            <p
                                v-if="scoredChoice.choice.report_message && includeReportFeedback"
                                class="mt-1 mb-2 medium text-secondary"
                            >
                                <span
                                    class="icon icon-100 icon-glyphicons-basic-lightbulb mr-1"
                                    style="vertical-align: 0px"
                                ></span>
                                <span class="text-response">{{ scoredChoice.choice.report_message }}</span>
                            </p>
                        </li>
                    </ul>
                </div>

                <!-- Discovered Intent List -->
                <div v-show="response.discovered_intents.length > 0">
                    <ul>
                        <li v-for="discoveredIntent in response.discovered_intents" :key="discoveredIntent.id">
                            <p class="mb-0">
                                <span class="align-middle">
                                    {{ discoveredIntent.message }}
                                </span>
                                <span
                                    class="align-middle ml-2"
                                    :class="{
                                        'text-success': discoveredIntent.score > 0,
                                        'text-danger': discoveredIntent.score < 0,
                                        'text-secondary': discoveredIntent.score == 0,
                                    }"
                                    v-show="discoveredIntent.score"
                                >
                                    {{ formatPointsDisplay(discoveredIntent.score) }} pts
                                </span>
                                <span
                                    class="icon icon-lg feedback-icon align-middle"
                                    :class="{
                                        'icon-glyphicons-basic-circle-empty-check text-success':
                                            discoveredIntent.correct === true,
                                        'icon-glyphicons-basic-circle-empty-remove text-danger':
                                            discoveredIntent.correct === false,
                                        'icon-glyphicons-basic-circle-empty-alert text-danger':
                                            discoveredIntent.was_missed,
                                    }"
                                ></span>
                                <span v-show="discoveredIntent.was_missed" class="tiny font-weight-500 text-danger">
                                    missed
                                </span>
                            </p>
                        </li>
                    </ul>
                </div>

                <!-- Text Response List -->
                <div v-for="textResponse in response.text_responses" :key="textResponse.id" class="mt-3">
                    <p class="mb-2">
                        <span v-show="!textResponse.is_edited_response">{{ labelsTranslated.yourResponse }}:</span>
                        <span v-show="textResponse.is_edited_response">{{ labelsTranslated.editedResponse }}:</span>
                    </p>
                    <p class="mb-2 border-left quote-border">
                        <em class="text-secondary text-response">{{ textResponse.text }}</em>
                    </p>
                    <text-response-ai-feedback
                        v-if="textResponse.ai_gen_feedback.feedback"
                        :panel="textResponse.panel"
                        :response="textResponse"
                        class="mt-3"
                    ></text-response-ai-feedback>
                    <ul class="mb-0">
                        <li
                            v-for="entity in textResponse.text_entities"
                            :key="entity.id"
                            v-show="entity.correct === true || (entity.was_discovered && entity.correct === false)"
                            class="mb-0"
                        >
                            <span class="align-middle mr-1">{{ entity.display_label }}</span>
                            <span
                                class="align-middle ml-1 mr-1"
                                :class="{
                                    'text-success': entity.score > 0,
                                    'text-danger': entity.score < 0,
                                    'text-secondary': entity.score == 0,
                                }"
                                v-show="entity.score"
                            >
                                {{ formatPointsDisplay(entity.score) }} pts
                            </span>
                            <span
                                v-show="entity.was_discovered && entity.correct === true"
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success"
                            ></span>
                            <span
                                v-show="entity.was_discovered && entity.correct === false"
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger"
                            ></span>
                            <span
                                v-show="!entity.was_discovered && entity.correct === true"
                                class="icon icon-lg icon-glyphicons-basic-circle-empty-alert feedback-icon align-middle text-danger"
                            ></span>
                        </li>
                    </ul>
                </div>

                <!-- File Responses -->
                <div v-for="fileResponse in response.voice_responses" :key="fileResponse.id" class="mt-3">
                    <audio
                        v-if="fileResponse.stored_file"
                        :src="fileResponse.stored_file.signed_url"
                        ref="audioPlayback"
                        controls
                    ></audio>
                    <div v-if="fileResponse.transcribed_text" class="text-secondary border-left quote-border my-4">
                        "{{ fileResponse.transcribed_text }}"
                    </div>
                    <text-response-ai-feedback
                        v-if="fileResponse.ai_gen_feedback.feedback"
                        :panel="fileResponse.panel"
                        :response="fileResponse"
                        class="mt-3"
                    ></text-response-ai-feedback>
                </div>

                <!-- Chat Conversation -->
                <div v-if="showChatLogs && response.chat_conversations.length">
                    <div v-for="chat_conversation in response.chat_conversations" :key="chat_conversation.id">
                        <div class="chat-conversation-container p-2 rounded" style="background-color: #fafafb;">
                            <chat-conversation :messages="chat_conversation.messages"></chat-conversation>
                        </div>
                    </div>
                </div>

                <!-- Chat Conversation Log (legacy) -->
                <div v-if="showChatLogs && response.chat_interactions.length">
                    <div class="mb-1 mt-4">
                        <p class="mb-0 medium">
                            <span>{{ labelsTranslated.chatConversation }}:</span>
                        </p>
                    </div>
                    <div v-for="interaction in response.chat_interactions" :key="interaction.id">
                        <div class="chat-interaction chat-learner rounded-lg my-2">
                            <p class="mb-0 medium">{{ interaction.user_input }}</p>
                        </div>
                        <div class="chat-interaction chat-agent rounded-lg my-2 text-right">
                            <p class="mb-0 medium">{{ interaction.avatar_response }}</p>
                        </div>
                    </div>
                </div>

                <!-- No Response Message -->
                <p v-show="response.is_empty" class="text-secondary font-weight-500 small pl-1">
                    {{ labelsTranslated.noResponse }}
                </p>

                <div v-if="index != results.responses.length - 1">
                    <hr />
                </div>
            </div>

            <div v-show="results.responses.length == 0" class="text-secondary text-center mb-3 mt-5">
                {{ labelsTranslated.noResponses }}
            </div>
        </div>

        <div v-if="showTimers">
            <div v-show="results.timers.length > 0" class="pt-5">
                <p class="h6 align-middle mb-4">
                    <span class="icon icon-glyphicons-basic-stopwatch mr-1"></span>
                    <span>{{ labelsTranslated.timeMetrics }}</span>
                </p>

                <div v-for="(timer, index) in results.timers" :key="timer.id">
                    <!-- Timer Label -->
                    <p class="mb-2">
                        <span class="badge badge-light reporting-badge align-middle mr-2">
                            <span class="icon icon-glyphicons-basic-stopwatch icon-mr"></span
                            >{{ timer.node_timer.label }}</span
                        >
                    </p>
                    <div>
                        <!-- Seconds Elapsed -->
                        <span class="small font-weight-bolder text-secondary mr-2 pr-1">
                            <span class="mr-2">{{ labelsTranslated.yourTime }}</span>
                            <span>{{ formatStopwatch(timer.elapsed_seconds_stopped) }}</span>
                        </span>
                        <!-- Time Exceeded -->
                        <span
                            class="small font-weight-bolder mr-1"
                            :class="{
                                'text-danger': timer.exceeded_seconds > 0,
                                'text-success': timer.exceeded_seconds <= 0,
                            }"
                        >
                            <span>(</span>
                            <span v-show="timer.exceeded_seconds > 0">+</span>
                            <span>{{ formatStopwatch(timer.exceeded_seconds) }}</span>
                            <span>)</span>
                        </span>
                        <!-- Score -->
                        <span
                            v-show="timer.score_received"
                            :class="{
                                'text-danger': timer.score_received < 0,
                                'text-success': timer.score_received > 0,
                            }"
                            class="small"
                        >
                            {{ formatPointsDisplay(timer.score_received) }} pts
                        </span>
                    </div>
                    <div v-if="index != results.timers.length - 1">
                        <hr />
                    </div>
                </div>
            </div>
        </div>

        <!-- Notepad Notes -->
        <div v-if="showNotes" :class="{ 'pt-5': showTimers || showBreakdown }">
            <p class="mb-3 h6 align-middle">
                <span class="icon icon-glyphicons-basic-notes mr-1"></span>
                <span>{{ labelsTranslated.notepadEntries }}</span>
            </p>
            <p class="mb-2 border-left quote-border" v-if="results.notes">
                <em class="text-secondary text-response">{{ results.notes }}</em>
            </p>
            <p v-else class="mb-2 text-secondary">{{ labelsTranslated.noNotes }}</p>
        </div>
    </div>
</template>

<script>
import TextResponseAiFeedback from "@/components/TextResponseAiFeedback"
import ChatConversation from "@/components/ChatConversation"
import Utilities from "@/assets/js/utilities.js"
import { labelTranslations } from "@/assets/js/settings.js"
import { panelTypeIcons } from "@/assets/js/settings.js"

export default {
    name: "LearnerResultsSummary",
    components: {
        TextResponseAiFeedback,
        ChatConversation,
    },
    props: {
        results: {
            type: Object,
            required: true,
        },
        displayTimestamps: {
            type: Boolean,
            default: false,
        },
        showChatLogs: {
            type: Boolean,
            default: false,
        },
        showTimers: {
            type: Boolean,
            default: true,
        },
        showBreakdown: {
            type: Boolean,
            default: true,
        },
        showNotes: {
            type: Boolean,
            required: false,
            default: true,
        },
        languageCode: {
            type: String,
            default: "en",
        },
        includeReportFeedback: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        labelsTranslated() {
            return labelTranslations[this.languageCode]
        },
    },
    methods: {
        displayDate(dateStr) {
            if (!dateStr) {
                return ""
            }
            return Utilities.formatDateTime(dateStr)
        },
        formatStopwatch(seconds) {
            return Utilities.formatStopwatch(Math.round(seconds))
        },
        formatPointsDisplay(points) {
            if (!points) {
                return points
            }
            if (points > 0) {
                return "+" + points
            }
            return points
        },
        getPanelIconClass(panel) {
            return panelTypeIcons[panel.panel_type]
        },
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.reporting-badge {
    text-align: left !important;
    white-space: normal !important;
    padding: 0.3em 0.45em;
    font-size: 80%;
    line-height: 1.2;
}

.chat-interaction {
    padding: 7px;
    padding-left: 14px;
    padding-right: 14px;
    max-width: 430px;
}

.chat-learner {
    background-color: #dee9ff;
}

.chat-agent {
    background-color: $light;
}

.chat-conversation-container {
    max-width: 400px;
}
</style>